import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { localize } from '@saviynt/common';

import { invertedMessages } from '../constants';
import fixLocaleEncoding from '../utils/fixLocaleEncoding';

export type InvertedLocalize<M extends string> = (
  defaultMessage: M,
  placeholders?: object
) => string;

/**
 * A custom hook that provides an inverted localization function.
 *
 * Unlike traditional localization where you provide a message ID to get a localized string,
 * this hook allows you to provide the default message directly and it looks up the corresponding ID.
 * This is useful when you have a set of predefined messages and want to reference them by their content
 * rather than by IDs.
 *
 * @template M - The type of messages that can be localized
 * @param {Record<M, string>} invertedLookup - A mapping of default messages to their corresponding IDs
 *                                            (defaults to the global invertedMessages)
 * @returns {InvertedLocalize<M>} A function that takes a default message and optional placeholders
 *                               and returns the localized string
 *
 * @example
 * const localize = useInvertedLocalize();
 *
 * // Use the default message directly
 * const greeting = localize('Hello, {name}!', { name: 'User' });
 *
 * @remarks
 * If the provided default message is not found in the invertedLookup,
 * it returns the default message prefixed with '&_' to indicate it's not localized.
 */
export const useInvertedLocalize = <M extends string>(
  invertedLookup: Record<M, string> = invertedMessages
): InvertedLocalize<M> => {
  const intl = useIntl();

  return useCallback(
    (defaultMessage: M, placeholders?: object): string => {
      const id = invertedLookup[defaultMessage];

      if (!id) {
        return `${defaultMessage}`;
      }

      return fixLocaleEncoding(
        localize(
          intl,
          { id, defaultMessage: `${defaultMessage}` },
          placeholders
        )
      );
    },
    [intl, invertedLookup]
  );
};
